import { useState, useEffect } from 'react';
import { Check, X, Clock, AlertCircle, MessageCircle } from 'lucide-react';
import { apiGet, apiPatch } from '../../services/api';
import toast from 'react-hot-toast';
import useAuthStore from '../../context/authStore';
import { BRAND } from '../../config/branding';

const statusConfig = {
  pending_client_review: { color: 'bg-yellow-100', textColor: 'text-yellow-700', label: 'Pending Your Review', icon: '⏳' },
  approved: { color: 'bg-green-100', textColor: 'text-green-700', label: 'Approved', icon: '✓' },
  rejected: { color: 'bg-red-100', textColor: 'text-red-700', label: 'Rejected', icon: '✗' },
};

export default function WageApprovalTab() {
  const { user } = useAuthStore();

  const [rateTiers, setRateTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.organizationId) {
      fetchRateTiers();
    }
  }, [user?.organizationId]);

  const fetchRateTiers = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/rate-tiers/${user.organizationId}/rate-tiers-approval`);
      // Filter to only show pending and previously approved/rejected
      const clientTiers = response.tiers.filter(
        t => t.approvalStatus === 'pending_client_review' || t.approvalStatus === 'approved' || t.approvalStatus === 'rejected'
      );
      setRateTiers(clientTiers);
    } catch (error) {
      toast.error('Failed to load rate tiers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedTier) return;

    try {
      setSubmitting(true);
      await apiPatch(`/rate-tiers/${selectedTier._id}/approve`, {
        approvalNotes: feedback || 'Approved',
      });

      toast.success('Rate tier approved');
      setShowApprovalModal(false);
      setFeedback('');
      setApprovalAction(null);
      fetchRateTiers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTier) return;

    if (!feedback.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setSubmitting(true);
      await apiPatch(`/rate-tiers/${selectedTier._id}/reject`, {
        approvalNotes: feedback,
      });

      toast.success('Rate tier rejected');
      setShowApprovalModal(false);
      setFeedback('');
      setApprovalAction(null);
      fetchRateTiers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateRates = (baseWorkerPayRate, locationAdj, profitMarkup) => {
    const workerPay = baseWorkerPayRate * (1 + (locationAdj || 0));
    const employerCost = workerPay * 0.18;
    const totalCost = workerPay + employerCost;
    const clientRate = totalCost * (1 + profitMarkup);
    const profit = clientRate - totalCost;
    const profitPercent = (profit / clientRate) * 100;

    return {
      workerPayRate: parseFloat(workerPay.toFixed(2)),
      employerCostPerHour: parseFloat(employerCost.toFixed(2)),
      totalCostPerHour: parseFloat(totalCost.toFixed(2)),
      clientBillingRate: parseFloat(clientRate.toFixed(2)),
      profitPerHour: parseFloat(profit.toFixed(2)),
      profitMarginPercent: parseFloat(profitPercent.toFixed(2)),
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pendingTiers = rateTiers.filter(t => t.approvalStatus === 'pending_client_review');
  const approvedTiers = rateTiers.filter(t => t.approvalStatus === 'approved');
  const rejectedTiers = rateTiers.filter(t => t.approvalStatus === 'rejected');

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Summary */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Rate Tier Approvals</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg border border-yellow-200 bg-yellow-50">
            <div className="text-sm text-yellow-700 mb-1">Awaiting Your Review</div>
            <div className="text-3xl font-bold text-yellow-900">{pendingTiers.length}</div>
          </div>
          <div className="p-4 rounded-lg border border-green-200 bg-green-50">
            <div className="text-sm text-green-700 mb-1">You Approved</div>
            <div className="text-3xl font-bold text-green-900">{approvedTiers.length}</div>
          </div>
          <div className="p-4 rounded-lg border border-red-200 bg-red-50">
            <div className="text-sm text-red-700 mb-1">Changes Requested</div>
            <div className="text-3xl font-bold text-red-900">{rejectedTiers.length}</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Pending Review Section */}
        {pendingTiers.length > 0 && (
          <div className="rounded-lg border border-yellow-200 p-4 bg-yellow-50">
            <h3 className="text-base font-bold text-yellow-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Awaiting Your Review ({pendingTiers.length})
            </h3>

            <div className="space-y-3">
              {pendingTiers.map((tier) => {
                const rates = calculateRates(
                  tier.baseWorkerPayRate,
                  0,
                  tier.profitMarkupPercentage
                );
                return (
                  <div
                    key={tier._id}
                    className="border border-yellow-200 rounded-lg p-4 bg-white hover:shadow-sm transition"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900">{tier.jobTitle}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Submitted: {new Date(tier.submittedForReviewDate).toLocaleDateString()}
                        </p>
                      </div>
                      {tier.clientReviewDeadline && (
                        <div className="text-right">
                          <p className="text-xs text-red-700 font-medium">Review by:</p>
                          <p className="text-sm text-red-900 font-bold">
                            {new Date(tier.clientReviewDeadline).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Rate Card */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Staff Rate</p>
                        <p className="font-bold text-gray-900">${rates.workerPayRate.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Your Cost</p>
                        <p className="font-bold text-gray-900">${rates.totalCostPerHour.toFixed(2)}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-600 mb-1">Billed Rate</p>
                        <p className="text-xl font-bold text-blue-600">${rates.clientBillingRate.toFixed(2)}/hr</p>
                      </div>
                    </div>

                    {/* Location Adjustments */}
                    {tier.locationAdjustments.length > 0 && (
                      <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                        <p className="text-xs text-gray-600 mb-2 font-medium">Location Adjustments:</p>
                        <div className="space-y-1">
                          {tier.locationAdjustments.map((loc, idx) => {
                            const locRates = calculateRates(
                              tier.baseWorkerPayRate,
                              loc.adjustment,
                              tier.profitMarkupPercentage
                            );
                            return (
                              <div key={idx} className="text-sm text-gray-700">
                                <span className="font-semibold">{loc.location}</span>
                                <span className="text-gray-600 ml-2">
                                  {loc.adjustment > 0 ? '+' : ''}{(loc.adjustment * 100).toFixed(1)}%
                                </span>
                                <span className="text-gray-600 ml-3">
                                  → ${locRates.clientBillingRate.toFixed(2)}/hr
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Differential / Premium Rates */}
                    {(tier.differentialRates || []).length > 0 && (
                      <div className="mb-4 p-3 bg-amber-50 rounded border border-amber-200">
                        <p className="text-xs text-amber-800 font-semibold mb-2">Differential / Premium Rates</p>
                        <div className="space-y-1">
                          {tier.differentialRates.map((dr, idx) => (
                            <div key={idx} className="flex justify-between text-sm text-amber-900">
                              <span>{dr.label || dr.type}</span>
                              <span className="font-semibold">${(rates.clientBillingRate * dr.multiplier).toFixed(2)}/hr ({dr.multiplier}×)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSelectedTier(tier);
                          setApprovalAction('approve');
                          setShowApprovalModal(true);
                        }}
                        style={{ backgroundColor: BRAND.primary }}
                        className="flex items-center gap-2 flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition font-medium text-sm"
                      >
                        <Check className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTier(tier);
                          setApprovalAction('reject');
                          setShowApprovalModal(true);
                        }}
                        className="flex items-center gap-2 flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium text-sm"
                      >
                        <X className="w-4 h-4" />
                        Request Changes
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Approved Section */}
        {approvedTiers.length > 0 && (
          <div className="rounded-lg border border-green-200 p-4 bg-green-50">
            <h3 className="text-base font-bold text-green-900 mb-4 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Approved Rates ({approvedTiers.length})
            </h3>

            <div className="space-y-3">
              {approvedTiers.map((tier) => {
                const rates = calculateRates(tier.baseWorkerPayRate, 0, tier.profitMarkupPercentage);
                return (
                  <div key={tier._id} className="p-4 bg-white border border-green-200 rounded">
                    <h3 className="font-bold text-gray-900 mb-2">{tier.jobTitle}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Worker Rate:</span>
                        <span className="ml-2 font-semibold">${rates.workerPayRate.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Your Cost:</span>
                        <span className="ml-2 font-semibold">${rates.totalCostPerHour.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Billed Rate:</span>
                        <span className="ml-2 font-bold" style={{ color: BRAND.primary }}>${rates.clientBillingRate.toFixed(2)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Approved on {new Date(tier.approvalDate).toLocaleDateString()}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Rejected Section */}
        {rejectedTiers.length > 0 && (
          <div className="rounded-lg border border-red-200 p-4 bg-red-50">
            <h3 className="text-base font-bold text-red-900 mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Changes Requested ({rejectedTiers.length})
            </h3>

            <div className="space-y-3">
              {rejectedTiers.map((tier) => {
                const rates = calculateRates(tier.baseWorkerPayRate, 0, tier.profitMarkupPercentage);
                return (
                  <div key={tier._id} className="p-4 bg-white border border-red-200 rounded">
                    <h3 className="font-bold text-gray-900 mb-2">{tier.jobTitle}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm mb-2">
                      <div>
                        <span className="text-gray-600">Worker Rate:</span>
                        <span className="ml-2 font-semibold">${rates.workerPayRate.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Your Cost:</span>
                        <span className="ml-2 font-semibold">${rates.totalCostPerHour.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Proposed Rate:</span>
                        <span className="ml-2 font-semibold">${rates.clientBillingRate.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="p-2 bg-red-100 border border-red-300 rounded text-sm text-red-900">
                      <p className="font-medium mb-1">Your Feedback:</p>
                      <p>{tier.approvalNotes}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Awaiting admin response</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {rateTiers.length === 0 && (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No rate tiers pending your review</p>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedTier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
              {approvalAction === 'approve' ? 'Approve Rate Tier?' : 'Request Changes?'}
            </h2>

            <div className="bg-gray-50 p-4 rounded mb-4">
              <p className="font-semibold text-gray-900">{selectedTier.jobTitle}</p>
              {(() => {
                const rates = calculateRates(
                  selectedTier.baseWorkerPayRate,
                  0,
                  selectedTier.profitMarkupPercentage
                );
                return (
                  <p className="text-sm text-gray-600 mt-1">
                    Billed at ${rates.clientBillingRate.toFixed(2)}/hr
                  </p>
                );
              })()}
            </div>

            {approvalAction === 'reject' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What changes do you need? *
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g., Rate is too high, we need it at $XX/hr instead..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  rows="3"
                />
              </div>
            )}

            {approvalAction === 'approve' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (Optional)
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g., Approved. Looking forward to staffing..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  rows="2"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setFeedback('');
                  setApprovalAction(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={approvalAction === 'approve' ? handleApprove : handleReject}
                disabled={submitting || (approvalAction === 'reject' && !feedback.trim())}
                style={{
                  backgroundColor: approvalAction === 'approve' ? BRAND.primary : undefined,
                }}
                className={`flex-1 px-4 py-2 text-white rounded-lg font-medium transition text-sm ${
                  approvalAction === 'approve'
                    ? 'hover:opacity-90 disabled:opacity-50'
                    : 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                }`}
              >
                {submitting ? 'Processing...' : (approvalAction === 'approve' ? 'Approve' : 'Request Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
